/**
 * §7.4 登入畫面（新增）。
 *
 * §5.2 最重要的一條：
 * 「`emailCode` 為 false 時，登入畫面**不得渲染**任何與驗證碼相關的元素
 *  ——不是 disable、不是隱藏後仍在 DOM，是根本不渲染。」
 *
 * 理由（規格書原話）：使用者不應該看到一個用不了的登入方式，
 * 那只會製造「是不是我操作錯」的困惑。
 */

import { useState } from 'react'
import { ApiError, NetworkError } from '../api/client.js'
import { Button, Field, FieldLabelSpacer, TextInput } from '../components/ui.js'
import { useAuth } from './AuthContext.js'

export function LoginScreen() {
  const { methods, notInitialized, login, loginWithCode, requestCode } = useAuth()
  const [mode, setMode] = useState<'password' | 'code'>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // §3.7 第 4 點：系統尚未初始化時顯示說明與指令，而不是一個登不進去的表單
  if (notInitialized) {
    return (
      <Shell>
        <h1 className="text-lg font-semibold mb-2">系統尚未初始化</h1>
        <p className="text-sm text-ink-600 mb-4">
          資料庫裡還沒有任何帳號。請在伺服器上設定初始管理者的 Email 後重啟服務。
        </p>
        <pre className="rounded bg-ink-900 text-ink-50 text-xs p-3 overflow-x-auto">
{`# 編輯 deploy/app.env
BOOTSTRAP_ADMIN_EMAIL=your.name@example.com

# 重啟並取得初始密碼
systemctl --user restart pv-app
podman logs pv-app | grep -A6 "初始管理者已建立"`}
        </pre>
      </Shell>
    )
  }

  if (!methods) {
    return <Shell><p className="text-sm text-ink-500">正在確認可用的登入方式…</p></Shell>
  }

  // 兩種都關是不允許的狀態（§5.2 的 LAST_LOGIN_METHOD 會擋），但真的發生時要講清楚
  if (!methods.password && !methods.emailCode) {
    return (
      <Shell>
        <h1 className="text-lg font-semibold mb-2">目前沒有可用的登入方式</h1>
        <p className="text-sm text-ink-600">
          密碼登入與 Email 驗證碼登入都處於停用狀態。請聯絡系統管理者，或用伺服器上的
          <code className="mx-1 rounded bg-ink-100 px-1">node src/cli.js</code>
          指令重新開啟其中一種。
        </p>
      </Shell>
    )
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (mode === 'password') await login(email.trim(), password)
      else await loginWithCode(email.trim(), code.trim())
    } catch (err) {
      // §8.6 message 直接顯示給使用者，不要換成「登入失敗」那種話
      setError(err instanceof ApiError || err instanceof NetworkError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const sendCode = async () => {
    if (!email.trim()) { setError('請先填 Email。'); return }
    setBusy(true)
    setError(null)
    try {
      await requestCode(email.trim())
      setCodeSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell>
      <img
        src="/logo.png"
        alt="PV Regen"
        width={320}
        height={234}
        className="mb-3 h-16 w-auto"
      />
      <h1 className="text-lg font-semibold mb-1">PV Regen 追溯系統</h1>
      <p className="text-xs text-ink-500 mb-5">請登入以繼續</p>

      {/* §5.2：只有兩種方式都可用時才需要切換器。單一方式就不要多這個東西 */}
      {methods.password && methods.emailCode && (
        <div className="mb-4 flex rounded-md border border-ink-300 p-0.5 text-sm">
          {([['password', '密碼登入'], ['code', 'Email 驗證碼']] as const).map(([m, label]) => (
            <button
              key={m}
              type="button"
              onClick={() => { setMode(m); setError(null) }}
              className={`flex-1 rounded px-3 py-1.5 ${
                mode === m ? 'bg-sky-700 text-white' : 'text-ink-700 hover:bg-ink-100'}`}
            >{label}</button>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="space-y-3">
        <Field label="Email" required>
          <TextInput
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.name@example.com"
          />
        </Field>

        {/* 密碼欄只在密碼登入可用時渲染 */}
        {methods.password && (mode === 'password' || !methods.emailCode) && (
          <Field label="密碼" required>
            <TextInput
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        )}

        {/*
          §5.2 emailCode 為 false 時，這整段連 DOM 都不存在。
          不是 hidden、不是 disabled——是根本不渲染。
        */}
        {methods.emailCode && (mode === 'code' || !methods.password) && (
          <>
            {/* 按鈕與輸入框對齊：見 FieldLabelSpacer 的說明。
                不能用 items-end——Field 的底部是提示文字的底部，不是輸入框的底部 */}
            <div className="flex gap-2">
              <Field label="驗證碼" hint="6 位數字，有效時間 10 分鐘" className="flex-1" required>
                <TextInput
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="tabular-nums tracking-[0.3em]"
                />
              </Field>
              <div className="shrink-0">
                <FieldLabelSpacer />
                <Button type="button" onClick={sendCode} disabled={busy}>
                  {codeSent ? '重寄' : '寄送驗證碼'}
                </Button>
              </div>
            </div>
            {codeSent && (
              <p className="text-xs text-emerald-700">
                若這個 Email 有對應的啟用帳號，驗證碼已經寄出。主旨列就會帶著驗證碼，不必開信。
              </p>
            )}
          </>
        )}

        {error && (
          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" className="w-full" disabled={busy}>
          {busy ? '請稍候…' : '登入'}
        </Button>
      </form>

      {/* §5.6 自助忘記密碼只在 emailCode 啟用時提供 */}
      {methods.emailCode && mode === 'password' && (
        <p className="mt-4 text-xs text-ink-500">
          忘記密碼？改用上方的「Email 驗證碼」登入，登入後到個人設定重設密碼。
        </p>
      )}
      {!methods.emailCode && (
        <p className="mt-4 text-xs text-ink-500">
          忘記密碼請聯絡系統管理者重設。
        </p>
      )}
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-100 p-4">
      <div className="w-full max-w-sm rounded-lg border border-ink-200 bg-white p-6 shadow-sm">
        {children}
      </div>
    </div>
  )
}
